// src/mixins/teacherProfileAppSyncMixin.js
//
// All TeacherProfile querying / syncing / subscriptions, moved off DataStore
// and onto AppSync (API.graphql). This is a mixin (not a plugin) on purpose:
// the logic relies heavily on the host component instance (this.$i18n,
// this.$vuetify, this.checkPayfastSubscription, this.syncData,
// this.dialogSessionWarning, this.newTeacherSessionToken, the mapState
// computeds, etc.), all of which keep working because mixin methods run with
// the component as `this`.

import { API, Auth } from "aws-amplify";
import _ from "lodash";
import store from "@/store";
import router from "@/router";
import { reconfigureDatastore } from "@/plugins/datastore/config.js";
import { teacherProfilesByOwner } from "@/graphql/queries";
import { updateTeacherProfile } from "@/graphql/mutations";
import {
  onCreateTeacherProfile,
  onUpdateTeacherProfile,
  onDeleteTeacherProfile,
} from "@/graphql/subscriptions";
import { parseGraphqlResult } from "@/core/utils/parseGraphqlResult";

export default {
  data() {
    return {
      teacherProfileCreateSub: null,
      teacherProfileUpdateSub: null,
      teacherProfileDeleteSub: null,
    };
  },

  methods: {
    // -----------------------------------------------------------------------
    // Entry point. Replaces the old DataStore.observeQuery(TeacherProfile).
    // Does a one-time owner-scoped query, then opens live subscriptions.
    // Safe to call multiple times (subscriptions are guarded; the
    // changingDatastoreConfig watcher re-calls this after a reconfigure).
    // -----------------------------------------------------------------------
    async syncTeacherProfile() {
      try {
        const user = await Auth.currentAuthenticatedUser().catch(() => {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
        });
        if (!user) return;

        // Owner string matches the format DataStore/AppSync stored it in.
        // If your owner field is NOT `sub::username`, swap teacherProfilesByOwner
        // for `listTeacherProfiles` below — owner @auth filters it either way.
        const owner = `${user.attributes.sub}::${user.username}`;

        let allItems = [];
        let nextToken = null;

        do {
          const result = await API.graphql({
            query: teacherProfilesByOwner,
            variables: {
              owner,
              filter: { _deleted: { ne: true } },
              nextToken, // null on first call
            },
            authMode: this.authMode,
          });

          const page = result.data.teacherProfilesByOwner;
          allItems = [...allItems, ...page.items];
          nextToken = page.nextToken;
        } while (nextToken);

        const items = parseGraphqlResult(allItems);

        // console.log("syncTeacherProfile items:", items);

        await this.handleTeacherProfileItems(items, true);

        // Open live subscriptions even when there is currently no profile,
        // so a brand-new user's onCreate is caught too.
        this.subscribeTeacherProfile();
      } catch (error) {
        console.log("syncTeacherProfile error:", error);
      }
    },

    subscribeTeacherProfile() {
      if (this.teacherProfileUpdateSub) return; // already subscribed

      this.teacherProfileCreateSub = API.graphql({
        query: onCreateTeacherProfile,
        authMode: this.authMode,
      }).subscribe({
        next: ({ value }) => {
          const created = parseGraphqlResult(value.data.onCreateTeacherProfile);
          if (created) this.handleTeacherProfileItems([created], true);
        },
        error: (error) =>
          console.log("onCreateTeacherProfile subscription error:", error),
      });

      this.teacherProfileUpdateSub = API.graphql({
        query: onUpdateTeacherProfile,
        variables: { filter: { _deleted: { ne: true } } },
        authMode: this.authMode,
      }).subscribe({
        next: ({ value }) => {
          const updated = parseGraphqlResult(value.data.onUpdateTeacherProfile);
          if (updated) this.handleTeacherProfileItems([updated], true);
        },
        error: (error) =>
          console.log("onUpdateTeacherProfile subscription error:", error),
      });

      this.teacherProfileDeleteSub = API.graphql({
        query: onDeleteTeacherProfile,
        authMode: this.authMode,
      }).subscribe({
        next: () => {
          store.commit("storeTeacherProfileModel", null);
          store.commit("storeSyncedTeacherProfile", true);
        },
        error: (error) =>
          console.log("onDeleteTeacherProfile subscription error:", error),
      });
    },

    // -----------------------------------------------------------------------
    // The "complicated thing". Ported 1:1 from the old observeQuery snapshot
    // handler, with DataStore.save(...) swapped for AppSync mutations.
    // Returns false ONLY on the very first profile-id discovery (when it kicks
    // off a DataStore reconfigure and bails); the changingDatastoreConfig
    // watcher then re-runs syncTeacherProfile, which re-enters here and
    // completes the rest (syncData / payfast / session token).
    // -----------------------------------------------------------------------
    async handleTeacherProfileItems(items, isSynced = true) {
      if (
        items.length > 1 &&
        !router.currentRoute.path.includes("/admin") &&
        !this.updatedSmartProductSort
      ) {
        const merged = await this.updateTeacherSmartProductSort(items);
        if (merged) items = [merged];
      }

      store.commit("storeSyncedTeacherProfile", !!isSynced);

      if (!items || items.length === 0) {
        store.commit("storeTeacherProfileModel", null);
        return true;
      }

      const newSyncedTPM = items[0];
      // console.log("newSyncedTPM:", newSyncedTPM);

      store.commit("storeTeacherProfileModel", newSyncedTPM);

      this.syncJustBoughtProducts(
        newSyncedTPM?.smartProductSort?.categories || [],
      );

      if (newSyncedTPM.translateI18n > "") {
        const newLang = newSyncedTPM.translateI18n;
        this.$vuetify.lang.current = newLang;
        this.$i18n.locale = newLang;
      }

      if (this.teacherProfileID !== newSyncedTPM.id) {
        store.commit("storeTeacherProfileID", newSyncedTPM.id);

        if (!this.previouslySyncedTeacherProfile) {
          this.previouslySyncedTeacherProfile = true;
          this.changedDatastorePredicate = true;

          if (this.datastoreReady && !this.changingDatastoreConfig) {
            await reconfigureDatastore();
            this.changedDatastorePredicate = false;
          }
          return false; // first discovery -> let the reconfigure flow re-run us
        }
      }

      if (
        !this.changingDatastoreConfig &&
        !this.teacherProductsOrderUpdateSub
      ) {
        this.syncData();
      }

      if (
        !this.initiallySynced ||
        this.teacherProfilePayfastSubscriptionToken !==
          newSyncedTPM.payfastSubscriptionToken
      ) {
        store.commit(
          "storeTeacherProfilePayfastSubscriptionToken",
          newSyncedTPM.payfastSubscriptionToken,
        );
        this.checkPayfastSubscription();
        this.initiallySynced = true;
      }

      if (typeof newSyncedTPM._version === "string") {
        if (this.teacherProfileAppSyncVersion !== newSyncedTPM._version) {
          store.commit(
            "storeTeacherProfileAppSyncVersion",
            newSyncedTPM._version,
          );
        }
      } else if (newSyncedTPM._version) {
        if (
          this.teacherProfileAppSyncVersion !== newSyncedTPM._version.toString()
        ) {
          store.commit(
            "storeTeacherProfileAppSyncVersion",
            newSyncedTPM._version.toString(),
          );
        }
      }

      if (this.teacherProfileProvince !== newSyncedTPM.province) {
        store.commit("storeTeacherProfileProvince", newSyncedTPM.province);
      }

      if (this.teacherProfileSchoolID !== newSyncedTPM.teacherProfileSchoolId) {
        store.commit(
          "storeTeacherProfileSchoolID",
          newSyncedTPM.teacherProfileSchoolId,
        );
      }

      const currentSessionTokenGet = JSON.parse(
        localStorage.getItem("teacherSessionToken"),
      );
      const currentSessionToken = currentSessionTokenGet
        ? currentSessionTokenGet.toString()
        : "";

      if (typeof newSyncedTPM.sessionToken === "string") {
        if (currentSessionToken === "") {
          this.newTeacherSessionToken = Date.now().toString();
          localStorage.setItem(
            "teacherSessionToken",
            this.newTeacherSessionToken,
          );
          await this.saveTeacherSessionToken(newSyncedTPM);
          store.commit("storeJustLoggedIn", true);
        } else if (newSyncedTPM.sessionToken !== currentSessionToken) {
          this.dialogSessionWarning = true;
        }
      } else if (this.newTeacherSessionToken === "") {
        this.newTeacherSessionToken = Date.now().toString();
        localStorage.setItem(
          "teacherSessionToken",
          this.newTeacherSessionToken,
        );
        await this.saveTeacherSessionToken(newSyncedTPM);
        store.commit("storeJustLoggedIn", true);
      }

      if (
        newSyncedTPM.payfastSubscriptionPackage?.proRataPaymentSuccessful ===
          true &&
        newSyncedTPM.sessionToken === currentSessionToken
      ) {
        const newPackage = _.cloneDeep(newSyncedTPM.payfastSubscriptionPackage);
        delete newPackage.proRataPaymentSuccessful;

        const recurring_amount = (
          (newPackage.grades.length > 1
            ? newPackage.perGrade * (newPackage.grades.length - 1) +
              newPackage.amount
            : newPackage.amount) * 100
        ).toFixed(0);

        const bodyToUpdate = {
          teacherProfileID: newSyncedTPM.id,
          package: newPackage,
          payfastSubscriptionToken: newSyncedTPM.payfastSubscriptionToken,
          recurring_amount,
        };

        await this.updatePayfastSubscription(bodyToUpdate);
      }

      return true;
    },

    async saveTeacherSessionToken(profile) {
      await API.graphql({
        query: updateTeacherProfile,
        variables: {
          input: {
            id: profile.id,
            _version: profile._version,
            sessionToken: this.newTeacherSessionToken,
          },
        },
        authMode: this.authMode,
      });
    },

    async updateTeacherSmartProductSort(items) {
      try {
        console.log(
          "Warning: More than one TeacherProfile found for the user.",
        );

        const newSyncedTPM = items[0];

        const newSmartProductSort = {
          categories: [...(newSyncedTPM.smartProductSort?.categories || [])],
          grades: [...(newSyncedTPM.smartProductSort?.grades || [])],
        };

        for (let i = 1; i < items.length; i++) {
          const duplicateTPM = items[i];

          newSmartProductSort.categories = [
            ...new Set([
              ...newSmartProductSort.categories,
              ...(duplicateTPM.smartProductSort?.categories || []),
            ]),
          ];
          newSmartProductSort.grades = [
            ...new Set([
              ...newSmartProductSort.grades,
              ...(duplicateTPM.smartProductSort?.grades || []),
            ]),
          ];
        }

        const result = await API.graphql({
          query: updateTeacherProfile,
          variables: {
            input: {
              id: newSyncedTPM.id,
              _version: newSyncedTPM._version,
              smartProductSort: JSON.stringify(newSmartProductSort),
            },
          },
          authMode: this.authMode,
        });

        this.updatedSmartProductSort = true;

        return parseGraphqlResult(result.data.updateTeacherProfile);
      } catch (error) {
        console.log("updateTeacherSmartProductSort error:", error);
        return items[0];
      }
    },
  },

  beforeDestroy() {
    if (this.teacherProfileCreateSub)
      this.teacherProfileCreateSub.unsubscribe();
    if (this.teacherProfileUpdateSub)
      this.teacherProfileUpdateSub.unsubscribe();
    if (this.teacherProfileDeleteSub)
      this.teacherProfileDeleteSub.unsubscribe();
  },
};
